import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Shield, Trash2, Plus } from "lucide-react";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useAdminOrgStore } from "@/stores/admin-org";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface SsoProvider {
  id: string;
  org_id: string;
  domain: string;
  provider_id: string;
  metadata_url: string;
  created_at: string;
}

export default function AdminSsoPage() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const adminOrgId = useAdminOrgStore((s) => s.orgId);
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const [metadataUrl, setMetadataUrl] = useState("");

  const orgsQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ["admin-organisations"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/organisations`, { headers });
      if (!res.ok) throw new Error("Failed to fetch organisations");
      return (await res.json()).data;
    },
  });

  const ssoQuery = useQuery<SsoProvider | null>({
    queryKey: ["admin-sso", adminOrgId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/organisations/${adminOrgId}/sso`, {
        headers,
      });
      if (!res.ok) throw new Error("Failed to fetch SSO config");
      return (await res.json()).data;
    },
    enabled: !!adminOrgId,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/organisations/${adminOrgId}/sso`, {
        method: "POST",
        headers,
        body: JSON.stringify({ domain: domain.toLowerCase(), metadata_url: metadataUrl }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? "Failed to register SSO provider");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sso", adminOrgId] });
      toast.success("SSO provider registered");
      setOpen(false);
      setDomain("");
      setMetadataUrl("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/organisations/${adminOrgId}/sso`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? "Failed to remove SSO provider");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sso", adminOrgId] });
      toast.success("SSO provider removed");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const selectedOrg = orgsQuery.data?.find((o) => o.id === adminOrgId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-medium">SSO Providers</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure SAML identity providers for organisations.
        </p>
      </div>

      {!adminOrgId && (
        <p className="text-sm text-muted-foreground">
          Select an organisation from the sidebar to manage its SSO configuration.
        </p>
      )}

      {adminOrgId && (
        <>
          <p className="text-sm">
            Organisation: <span className="font-medium">{selectedOrg?.name ?? adminOrgId}</span>
          </p>

          {ssoQuery.isLoading && <Skeleton className="h-20 w-full rounded" />}

          {ssoQuery.data ? (
            <div className="flex items-center justify-between rounded-md border p-4">
              <div className="flex items-center gap-3">
                <Shield className="size-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium">{ssoQuery.data.domain}</p>
                  <p className="text-xs text-muted-foreground">{ssoQuery.data.metadata_url}</p>
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="text-red-500 hover:text-red-700"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ) : (
            ssoQuery.isFetched && (
              <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
                <Shield className="size-10 text-gray-300" />
                <p className="text-sm">No SSO provider configured</p>
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="mr-1 size-4" />
                      Add SSO Provider
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Register SAML SSO Provider</DialogTitle>
                    </DialogHeader>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        createMutation.mutate();
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="text-sm font-medium" htmlFor="sso-domain">
                          Email Domain
                        </label>
                        <Input
                          id="sso-domain"
                          placeholder="e.g. company.com"
                          value={domain}
                          onChange={(e) => setDomain(e.target.value)}
                          required
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                          Users with this email domain will be redirected to the IdP.
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium" htmlFor="sso-metadata">
                          SAML Metadata URL
                        </label>
                        <Input
                          id="sso-metadata"
                          placeholder="https://idp.company.com/metadata"
                          value={metadataUrl}
                          onChange={(e) => setMetadataUrl(e.target.value)}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={createMutation.isPending}>
                        {createMutation.isPending ? "Registering..." : "Register Provider"}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
